import { supabase } from '@/lib/supabase';
import { ParsedChannelData } from '@/features/channels/types/import';
import { BusinessError } from '@/features/channels/utils/errors';
import { logger, LOG_CONTEXTS } from '@/utils/logger';
import { toast } from 'sonner';

/**
 * Service for handling sequential provider imports
 */
export class SequentialImportService {
  async getNextSequenceNumber(providerId: number, versionId: number): Promise<number> {
    const { data, error } = await supabase
      .rpc('get_next_sequence_number', {
        p_provider_id: providerId,
        p_version_id: versionId
      });

    if (error) {
      toast.error("Failed to get next sequence number for import");
      throw new BusinessError('Failed to get next sequence number');
    }

    return data;
  }

  async generateSequentialNumbers(
    data: ParsedChannelData[],
    providerId: number,
    versionId: number
  ): Promise<ParsedChannelData[]> {
    // First get the sequence type
    let typeData: any;

    const { data: existingType, error: typeError } = await supabase
      .from('channel_types')
      .select('*')
      .eq('provider_id', providerId)
      .eq('version_id', versionId)
      .eq('is_sequence', true)
      .single();

    if (typeError) {
      toast.error("Failed to get sequence type for this version");
      throw new BusinessError('Failed to get sequence type for this version');
    }

    if (!existingType) {
      // If no sequence type exists, create one
      const { data: newType, error: createError } = await supabase
        .from('channel_types')
        .insert({
          name: 'Serial No',
          provider_id: providerId,
          version_id: versionId,
          is_sequence: true,
          is_default: true,
          display_order: 0
        })
        .select()
        .single();

      if (createError) {
        toast.error("Failed to create sequence type for import");
        throw new BusinessError('Failed to create sequence type');
      }

      typeData = newType;
    } else {
      typeData = existingType;
    }

    // Get next sequence number using existing function
    const nextNumber = await this.getNextSequenceNumber(providerId, versionId);
    let currentNumber = nextNumber;

    // Map the data with sequential numbers
    const numberedData = data.map((item: ParsedChannelData): ParsedChannelData => ({
      ...item,
      numbers: [{
        typeId: typeData.id,
        number: String(currentNumber++),
        isSequence: true,
        is_default: typeData.is_default || false
      }],
      name: item.name,
      line: item.line,
      categoryId: item.categoryId,
      planIds: item.planIds || []
    }));

    // Note: We'll handle assign_import_numbers after the channels are actually created
    // This will be done in the import process, not here in the number generation
    return numberedData;
  }

  /**
   * Get or create a sequence type for a provider
   */
  async getSequenceTypeId(providerId: number, versionId: number): Promise<number> {
    try {
      logger.info(LOG_CONTEXTS.IMPORT, 'Getting sequence type for provider', { providerId, versionId });
      
      // First check if provider is sequential
      const { data: provider, error: providerError } = await supabase
        .from('providers')
        .select('has_sequence_numbers')
        .eq('id', providerId)
        .single();
        
      if (providerError) {
        logger.error(LOG_CONTEXTS.IMPORT, 'Error getting provider', { error: providerError });
        throw new Error('Failed to get provider details');
      }
      
      if (!provider.has_sequence_numbers) {
        logger.error(LOG_CONTEXTS.IMPORT, 'Provider does not support sequence numbers', { providerId });
        throw new Error('Provider does not support sequence numbers');
      }
      
      // Look for existing sequence type
      const { data: existingType, error: typeError } = await supabase
        .from('channel_types')
        .select('id')
        .eq('provider_id', providerId)
        .eq('version_id', versionId)
        .eq('is_sequence', true)
        .single();
        
      if (!typeError && existingType) {
        logger.info(LOG_CONTEXTS.IMPORT, 'Found existing sequence type', { typeId: existingType.id });
        return existingType.id;
      }
      
      // No existing type, create one
      logger.info(LOG_CONTEXTS.IMPORT, 'Creating new sequence type');
      const { data: newType, error: createError } = await supabase
        .from('channel_types')
        .insert({
          name: 'Serial No',
          provider_id: providerId,
          version_id: versionId,
          is_sequence: true,
          is_default: false,
          display_order: 1
        })
        .select()
        .single();
        
      if (createError) {
        logger.error(LOG_CONTEXTS.IMPORT, 'Failed to create sequence type', { error: createError });
        throw new Error('Failed to create sequence type');
      }
      
      logger.info(LOG_CONTEXTS.IMPORT, 'Created new sequence type', { typeId: newType.id });
      return newType.id;
    } catch (error) {
      logger.error(LOG_CONTEXTS.IMPORT, 'Error in getSequenceTypeId', { error });
      throw error;
    }
  }

  /**
   * Process a bulk import for a sequential provider with enhanced monitoring
   */
  async processBulkImport(
    data: any[], 
    providerId: number, 
    versionId: number, 
    categoryId: number, 
    planIds: number[] = [],
    isLocal: boolean = false,
    isDuplicate: boolean = false
  ) {
    try {
      logger.info(LOG_CONTEXTS.IMPORT, 'Starting sequential bulk import', { 
        itemCount: data.length,
        providerId,
        versionId,
        categoryId,
        originalPlanIds: planIds
      });
      
      // First validate plan IDs - check provider isolation (critical)
      let validPlanIds: number[] = [];
      
      if (planIds && planIds.length > 0) {
        logger.info(LOG_CONTEXTS.IMPORT, 'Validating plan IDs', {
          planIds,
          providerId,
          versionId
        });
        
        // First ensure all plans belong to the current provider (strict provider isolation)
        const { data: providerPlans, error: providerError } = await supabase
          .from('plans')
          .select('id, name, provider_id, version_id')
          .in('id', planIds);
          
        if (providerError) {
          logger.error(LOG_CONTEXTS.IMPORT, 'Error checking plan provider', { providerError });
          // Continue with empty plan IDs rather than failing
          validPlanIds = [];
        } else if (!providerPlans || providerPlans.length === 0) {
          logger.warn(LOG_CONTEXTS.IMPORT, 'No valid plans found', { 
            attemptedPlanIds: planIds
          });
          validPlanIds = [];
        } else {
          // First filter by provider (critical isolation check)
          const providerFilteredPlans = providerPlans.filter(plan => plan.provider_id === providerId);
          
          if (providerFilteredPlans.length !== providerPlans.length) {
            const plansFromOtherProviders = providerPlans.filter(plan => plan.provider_id !== providerId);
            logger.error(LOG_CONTEXTS.IMPORT, 'Critical: Plans from other providers detected', {
              plansFromOtherProviders: plansFromOtherProviders.map(p => ({ id: p.id, name: p.name, providerId: p.provider_id }))
            });
          }
          
          // Then filter by version (expected potential mismatch)
          const versionFilteredPlans = providerFilteredPlans.filter(plan => plan.version_id === versionId);
          validPlanIds = versionFilteredPlans.map(p => p.id);
          
          if (versionFilteredPlans.length !== providerFilteredPlans.length) {
            const plansFromOtherVersions = providerFilteredPlans.filter(plan => plan.version_id !== versionId);
            logger.warn(LOG_CONTEXTS.IMPORT, 'Some plan IDs are from different versions', {
              providerId,
              versionId,
              validPlanIds,
              validPlanNames: versionFilteredPlans.map(p => p.name),
              plansFromOtherVersions: plansFromOtherVersions.map(p => ({ id: p.id, name: p.name, versionId: p.version_id })),
              totalOriginalPlans: planIds.length,
              validProvidersCount: providerFilteredPlans.length,
              validVersionsCount: versionFilteredPlans.length
            });
          } else {
            logger.info(LOG_CONTEXTS.IMPORT, 'All plan IDs are valid for this provider and version', {
              validPlanIds,
              validPlanNames: versionFilteredPlans.map(p => p.name)
            });
          }
        }
      }
      
      // Create a requestId for tracking this operation
      const requestId = logger.createRequestId();
      
      // Only proceed with valid plan IDs
      const startTime = performance.now();

      // CRITICAL: Ensure all parameters are explicitly defined (not undefined)
      // Supabase removes undefined values, causing PostgREST to not find the function
      const rpcParams = {
        p_category_ids: categoryId ? [categoryId] : [],
        p_data: data,
        p_default_category_id: categoryId || null,
        p_is_duplicate: isDuplicate || false,
        p_is_local: isLocal || false,
        p_plan_ids: validPlanIds || [],
        p_provider_id: providerId,
        p_version_id: versionId
      };

      logger.debug(LOG_CONTEXTS.IMPORT, 'RPC parameters being sent', { rpcParams }, requestId);

      const { data: result, error } = await (supabase as any).rpc(
        'process_sequential_channel_import',
        rpcParams
      );
      const endTime = performance.now();
      
      if (error) {
        logger.error(LOG_CONTEXTS.IMPORT, 'Error in sequential bulk import', { 
          error,
          providerId,
          versionId
        }, requestId);
        throw error;
      }
      
      // Log detailed information about the result
      const resultSummary = Array.isArray(result) 
        ? {
            count: result.length,
            firstItem: result[0],
            lastItem: result[result.length - 1],
            processingTime: `${(endTime - startTime).toFixed(2)}ms`
          } 
        : { result, processingTime: `${(endTime - startTime).toFixed(2)}ms` };
      
      logger.info(LOG_CONTEXTS.IMPORT, 'Sequential bulk import completed', resultSummary, requestId);

      // Check for any failures in the result
      if (Array.isArray(result)) {
        const failures = result.filter(r => !r.success);
        if (failures.length > 0) {
          logger.error(LOG_CONTEXTS.IMPORT, 'Some channels failed to import', {
            failureCount: failures.length,
            totalCount: result.length,
            failures: failures.map(f => ({ 
              channel_id: f.channel_id, 
              error: f.error_message 
            }))
          }, requestId);
        }
      }

      // Track channel entity creation for bulk import
      try {
        // Get version data for region_id
        const { data: version } = await supabase
          .from('provider_versions')
          .select('region_id')
          .eq('id', versionId)
          .single();

        if (version && Array.isArray(result) && result.length > 0) {
          // Filter successful results and track them
          const successfulResults = result.filter(r => r.success && r.channel_id);
          
          if (successfulResults.length > 0) {
            const { changeTrackingService } = await import('../changeTracking');

            for (let i = 0; i < successfulResults.length; i++) {
              const resultItem = successfulResults[i];
              const originalData = data[i]; // Match with original data by index

              if (resultItem.channel_id && originalData) {
                await changeTrackingService.logEntityCreation({
                  versionId,
                  providerId,
                  regionId: version.region_id,
                  entityType: 'channels',
                  entityId: resultItem.channel_id,
                  entityData: {
                    id: resultItem.channel_id,
                    name: originalData.name,
                    description: originalData.description,
                    price: originalData.price,
                    category_id: categoryId,
                    is_local: isLocal || false,
                    is_duplicate: isDuplicate || false
                  }
                });
              }
            }

            logger.info(LOG_CONTEXTS.IMPORT, 'Tracked bulk channel creation', {
              channelCount: successfulResults.length,
              providerId,
              versionId
            });
          }
        }
      } catch (trackingError) {
        logger.error(LOG_CONTEXTS.IMPORT, 'Failed to track bulk channel creation', {
          error: trackingError,
          providerId,
          versionId
        });
      }

      // Verify the channels were actually created
      const verifyStartTime = performance.now();
      const { data: createdChannels, error: verifyError } = await supabase
        .from('channels')
        .select('id')
        .eq('provider_id', providerId)
        .eq('version_id', versionId)
        .eq('category_id', categoryId)
        .order('created_at', { ascending: false })
        .limit(data.length);
      const verifyEndTime = performance.now();
      
      if (verifyError) {
        logger.error(LOG_CONTEXTS.IMPORT, 'Error verifying created channels', { 
          error: verifyError 
        }, requestId);
      } else {
        logger.info(LOG_CONTEXTS.IMPORT, 'Created channels verification', { 
          expected: data.length,
          found: createdChannels?.length || 0,
          verifyTime: `${(verifyEndTime - verifyStartTime).toFixed(2)}ms`
        }, requestId);
      }
      
      // Verify and fix any data issues
      try {
        await supabase.rpc('fix_channel_data_issues', {
          p_provider_id: providerId,
          p_version_id: versionId
        });
        logger.info(LOG_CONTEXTS.IMPORT, 'Fixed any channel data issues');
      } catch (fixError) {
        logger.error(LOG_CONTEXTS.IMPORT, 'Error fixing channel data issues', { fixError });
      }
      
      return result;
    } catch (error) {
      logger.error(LOG_CONTEXTS.IMPORT, 'Error in processBulkImport', { error });
      throw error;
    }
  }
}

// Export a singleton instance for compatibility with existing code
export const sequentialImportService = new SequentialImportService(); 